import React from 'react';
import { HeroSkeleton } from '@hotelplan/components.common.hero';
import { TextBlock } from '@hotelplan/components.common.text-block';
import { BsHero } from '@hotelplan/core.basis.bs-hero';
import { BsIcon } from '@hotelplan/core.basis.bs-icon';
import { AuthChannelType } from '@hotelplan/fdr.lib.ident.auth-config';
import { useAuthentication } from '@hotelplan/fdr.lib.ident.with-auth';
import { EPageType } from '@hotelplan/fdr.lib.page.available-pages';
import {
  FdrPicture,
  FdrPicturePreload,
} from '@hotelplan/fdr.regular.basis.fdr-picture';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { usePageType } from '@hotelplan/libs.context.page-type';
import { FdrImage } from '@hotelplan/supergraph-api';
import FdrBookmarkBlock from 'components/domain/fdr-bookmark/fdr-bookmark-block';
import { bookmarkablePageTypes } from 'components/domain/fdr-bookmark/fdr-bookmark-types';
import HeroOgTags from 'components/domain/fdr-layout/fdr-hero-og-tags';
import { checkProductType } from 'components/domain/fdr-pdp';
import FdrSharePdf from 'components/domain/fdr-share-pdf/fdr-share-pdf';
import FdrShareSiteTool from 'components/domain/fdr-site-tool/fdr-share-site-tool';
import GeoTextBlock from 'components/domain/geo/GeoTextBlock';
import { HeroToolsWrap } from 'components/domain/sitetools/SiteTools.common';
import { useShareUrl } from 'components/domain/sitetools/use-share-url';
import { BookmarkComponentType } from 'components/domain/tracking/bookmarkTracking';
import {
  trackPdpShareSiteToolClick,
  trackPdpShareSiteToolOpenClick,
} from 'components/domain/tracking/pdpTracking';
import { fdrHeroImagesSpecs } from 'components/util/criterias/hero.dmc';
import {
  HeroImage,
  HeroImageSkeleton,
  HeroLayoutWrapper,
  HeroStaticText,
  PdpScrollDownButton,
  PdpScrollDownWrapper,
} from './fdr-hero-section.styles';

interface IHeroDefaultProps {
  title: string;
  text: string;
  hero: FdrImage;
  loading?: boolean;
  isHeroSiteToolsEnabled?: boolean;
  bookmarked?: boolean;
}

const excludePageType = [
  EPageType.NewsletterConfirmation,
  EPageType.NewsletterFinalization,
  EPageType.NewsletterUnsubscription,
  EPageType.NewsletterUnsubscriptionFinalization,
  EPageType.EmailAdvertisingRevocation,
  EPageType.EmailAdvertisingRevocationConfirmation,
];

const Buttons: React.FC<{
  title: string;
  hero: FdrImage;
  isHeroSiteToolsEnabled?: boolean;
  bookmarked?: boolean;
}> = ({ bookmarked, isHeroSiteToolsEnabled, title, hero }) => {
  const pageType = usePageType<EPageType>();
  const { id } = useObjectId();
  const { channelType } = useAuthentication();
  const isB2b = channelType === AuthChannelType.B2B;
  const isPdP = pageType === EPageType.Pdp;
  const shareUrl = useShareUrl();

  const trackFns = isPdP
    ? {
        trackOpen: trackPdpShareSiteToolOpenClick,
        trackShare: trackPdpShareSiteToolClick,
      }
    : undefined;

  return (
    <HeroToolsWrap className="hero-tools">
      {excludePageType.includes(pageType) || !isHeroSiteToolsEnabled ? null : (
        <FdrShareSiteTool
          pageTitle={title}
          image={hero}
          url={shareUrl}
          trackFns={trackFns}
        />
      )}
      {isB2b && isPdP && <FdrSharePdf hero={hero} title={title} />}
      {bookmarkablePageTypes.includes(pageType) ? (
        <FdrBookmarkBlock
          objectId={id}
          active={bookmarked}
          trackingComponent={BookmarkComponentType.CONTENT}
        />
      ) : null}
    </HeroToolsWrap>
  );
};

export const FdrHeroSection: React.FC<IHeroDefaultProps> = ({
  text,
  title,
  hero,
  loading,
  isHeroSiteToolsEnabled = true,
  children,
  bookmarked,
}) => {
  const pageType = usePageType<EPageType>();
  const { id } = useObjectId();
  const isPdP = pageType === EPageType.Pdp;

  const { mobile } = useDeviceType();

  const scrollToPdpDescription = () => {
    const pdpDescriptionBlock = document.getElementById(
      'pdp-description-block'
    );
    if (!pdpDescriptionBlock) return;
    pdpDescriptionBlock.scrollIntoView({ behavior: 'smooth' });
  };
  const { isHotel } = checkProductType(id);

  return (
    <>
      <HeroOgTags image={hero} />
      <HeroStaticText className="HeroStaticText">
        {pageType === EPageType.GeoObject ? (
          <GeoTextBlock title={title} text={text} />
        ) : (
          <TextBlock
            title={title}
            text={text}
            className={
              isPdP
                ? 'home-static-text home-static-text-pdp'
                : 'home-static-text'
            }
          />
        )}
      </HeroStaticText>
      {loading ? (
        <HeroSkeleton />
      ) : (
        <HeroLayoutWrapper
          className={`${isPdP ? 'is-pdp' : ''} ${isHotel ? 'is-hotel' : ''}`}
        >
          <BsHero
            className={`hero-wrapper ${isPdP ? 'is-pdp' : ''} ${
              isHotel ? 'is-hotel' : ''
            }`}
            controls={
              <Buttons
                hero={hero}
                title={title}
                bookmarked={bookmarked}
                isHeroSiteToolsEnabled={isHeroSiteToolsEnabled}
              />
            }
          >
            {hero && (
              <FdrPicturePreload
                resized={hero.resized}
                sources={fdrHeroImagesSpecs}
              />
            )}
            {hero ? (
              <FdrPicture sources={fdrHeroImagesSpecs} sizes={hero.resized}>
                <HeroImage image={hero} lazy={true} noSet />
              </FdrPicture>
            ) : (
              <HeroImageSkeleton />
            )}
          </BsHero>
          {children}
          {isPdP && !mobile ? (
            <PdpScrollDownWrapper
              onClick={scrollToPdpDescription}
              className={isHotel ? 'is-hotel' : ''}
            >
              <PdpScrollDownButton>
                <BsIcon name="chevron-down" className="scroll icon" />
              </PdpScrollDownButton>
            </PdpScrollDownWrapper>
          ) : null}
        </HeroLayoutWrapper>
      )}
    </>
  );
};
